/**
 * @file 支付弹窗组件
 */
import React, {useCallback, useEffect, useMemo, useState} from 'react';
import {PaymentPanel} from '../../payment/PaymentPanel/index';
import type {PaymentPanelEvent} from '../../payment/PaymentPanel/index';
import {usePaymentSession} from '../../payment/hooks/usePaymentSession';
import {refreshMembershipData} from './hooks/useMemberShip';
import styles from './PaymentModal.module.less';
import {PaySuccessImgUrl} from './constants';
import {useMembershipTracking} from './hooks/useMembershipTracking';
import {TrackEvents} from '@/services/tracking/constant';
import Modal from '@/components/common/Modal';
import Tooltip from '@/components/common/Tooltip';
import Button from '@/components/common/Button';
import Icon from '@/components/common/Icon';
import {OrderTypeValue, type MemberLevelItem} from '@/services/commerce/membership/interface';
import {createMembershipOrder} from '@/services/commerce/payment';
import type {Order} from '@/services/commerce/entities/payment';
import type {SubscriptionType} from '@/services/commerce/type';
import {refreshBenefitProfile} from '@/commerce/benefit';
import {PLATFORM_EVENT_NAMES, platformEventBus} from '@/platform/events';
import {formatUnixDate} from '@/utils/date';
import {invalidatePointsSummary} from '@/hooks/points/usePersonalPoints';
import {useMemberExtShowLog} from '@/hooks/memberLogExt';

interface PaymentModalProps {
    open: boolean;
    level: MemberLevelItem;
    subscriptionType: SubscriptionType;
    /** 订阅周期名称（plan.typeName） */
    subscriptionName?: string;
    onClose: () => void;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
    open,
    level,
    subscriptionType,
    subscriptionName,
    onClose,
}) => {
    // 是否显示支付成功界面
    const [showPaySuccess, setShowPaySuccess] = useState(false);
    const {sendShowLog, sendClickLog} = useMembershipTracking({
        selectedLevel: level,
        activeTab: subscriptionType,
    });
    // 权益下发中 tip（PAID 但 RIGHTS_ISSUED 尚未到达时展示）
    const [showRightsPendingTip, setShowRightsPendingTip] = useState(false);

    useMemberExtShowLog(
        open &&
            showPaySuccess && {
                page: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
                value: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL_PAY_SUCCESS,
            },
        sendShowLog,
    );

    // 缓存支付成功结果
    const [paySuccessResult, setPaySuccessResult] = useState<Order['sku'] | null>(null);

    /** 刷新会员相关数据（权益/等级/积分）+ 通知其他模块 */
    const refreshAllMembershipData = useCallback(() => {
        refreshBenefitProfile();
        refreshMembershipData();
        void invalidatePointsSummary();
        platformEventBus.emit(PLATFORM_EVENT_NAMES.POINTS_RECHARGE_SUCCESS, {
            entry: 'agent',
        });
    }, []);

    const session = usePaymentSession({
        createOrder: createMembershipOrder,
        onSuccess: (result: Order['sku'], rightsIssued: boolean) => {
            // 支付成功，展示成功页
            setShowPaySuccess(true);
            setPaySuccessResult(result);
            if (rightsIssued) {
                refreshAllMembershipData();
            } else {
                // 权益尚未下发，展示 tip
                setShowRightsPendingTip(true);
            }
        },
        onRightsIssued: (result) => {
            setPaySuccessResult(result);
            // 权益下发完成，隐藏 tip + 刷新数据
            setShowRightsPendingTip(false);
            refreshAllMembershipData();
        },
    });

    useEffect(() => {
        // 开启支付弹窗时下单
        if (open && level.sku.skuId) {
            session.start({orderType: 'membership', skuId: level.sku.skuId});
        }
    }, [open, level.sku.skuId]); // eslint-disable-line react-hooks/exhaustive-deps

    // 重置支付状态&权益下发tip
    const resetPayModalState = useCallback(() => {
        session.cancel();
        setShowPaySuccess(false);
        setPaySuccessResult(null);
        setShowRightsPendingTip(false);
    }, [session]);

    // 支付弹窗关闭时重置支付状态
    const handleCancelAndReset = useCallback(() => {
        // 如果是 success 但权益未下发（用户提前关了），延迟 500ms 兜底刷新
        if (session.isPendingRights) {
            setTimeout(refreshAllMembershipData, 500);
        }
        resetPayModalState();

        onClose?.();
    }, [onClose, resetPayModalState]); // eslint-disable-line react-hooks/exhaustive-deps

    // 订单sku名称：优先用接口返回的 sku 名称，未返回时用「档位名 · 订阅周期」组合
    const skuName = useMemo(() => {
        if (showPaySuccess) {
            return '';
        }
        const orderSkuName = 'order' in session.state ? session.state.order.sku.skuName : '';
        return orderSkuName || [level.levelName, subscriptionName].filter(Boolean).join(' · ');
    }, [session.state, level.levelName, subscriptionName, showPaySuccess]);

    // 协议文本
    const agreement = useMemo(() => {
        return (
            <div className={styles.agreement}>
                <span>开通即同意</span>
                <a href="/membershipAgreement" target="_blank" rel="noopener noreferrer">
                    《会员与积分服务协议》
                </a>
            </div>
        );
    }, []);

    // 提示框状态
    const [tooltipOpen, setTooltipOpen] = useState(false);
    const openTooltip = useCallback(() => {
        setTooltipOpen(true);
    }, [setTooltipOpen]);

    // 关闭提示
    const closeTooltip = useCallback(() => {
        setTooltipOpen(false);
    }, [setTooltipOpen]);

    // 再想想按钮点击
    const handleReconsideration = useCallback(() => {
        closeTooltip();
        sendClickLog({
            page: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
            value: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL_CLOSE_CONFIRM_RECONSIDER,
        });
    }, [closeTooltip, sendClickLog]);

    // 确认关闭
    const confirmClose = useCallback(() => {
        closeTooltip();
        handleCancelAndReset();
        sendClickLog({
            page: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
            value: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL_CLOSE_CONFIRM_EXIT,
        });
    }, [handleCancelAndReset, closeTooltip, sendClickLog]);

    // 支付面板关闭
    const handlePaymentPanelClose = useCallback(() => {
        if (showPaySuccess) {
            handleCancelAndReset();
        } else {
            openTooltip();
        }
    }, [handleCancelAndReset, showPaySuccess, openTooltip]);

    // PaymentPanel 事件处理
    const handlePaymentEvent = useCallback(
        (event: PaymentPanelEvent) => {
            // 二维码重试展现埋点
            if (event.type === 'retry-show') {
                sendShowLog({
                    page: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL,
                    value: TrackEvents.MEMBERSHIP_PAY_QRCODE_MODAL_RETRY,
                });
            }
        },
        [sendShowLog],
    );

    const closeIcon = useMemo(() => {
        return (
            <Tooltip
                open={tooltipOpen}
                styles={{
                    container: {
                        width: '330px',
                    },
                }}
                title={
                    <div className={styles.closeContent}>
                        <div className={styles.tips}>
                            确认取消支付吗？离开后当前限时优惠可能会失效，再次订阅可能需按原价支付。
                        </div>
                        <div className={styles.btns}>
                            <Button type="default" onClick={handleReconsideration}>
                                再想想
                            </Button>
                            <Button type="primary" onClick={confirmClose}>
                                确认
                            </Button>
                        </div>
                    </div>
                }
                trigger="click"
            >
                <Icon
                    name="AijBasicGuanbi"
                    size={16}
                    onClick={handlePaymentPanelClose}
                    className={styles.closeIcon}
                />
            </Tooltip>
        );
    }, [tooltipOpen, confirmClose, handleReconsideration, handlePaymentPanelClose]);

    // 弹窗关闭时重置支付状态
    useEffect(() => {
        if (!open) {
            resetPayModalState();
        }
    }, [open, resetPayModalState]);

    return (
        <Modal
            open={open}
            title={skuName}
            width={440}
            footer={null}
            mask={{closable: showPaySuccess}}
            destroyOnHidden
            className={styles.modal}
            closeIcon={closeIcon}
        >
            <div className={styles.content}>
                {showPaySuccess ? (
                    <div className={styles.paySuccessContainer}>
                        <div className={styles.imgWrapper}>
                            <img
                                src={PaySuccessImgUrl}
                                alt="pay success"
                                className={styles.paySuccessImg}
                            />
                            <div className={styles.successText}>支付成功</div>
                        </div>

                        <div className={styles.desc}>
                            <span>
                                恭喜成功
                                {level.orderType === OrderTypeValue.UPGRADE ? '升级至' : '订阅'}
                            </span>
                            <span className={styles.levelName}> {paySuccessResult?.skuName}</span>
                        </div>
                        {paySuccessResult?.pointsIsImmediate && paySuccessResult.points && (
                            <div className={styles.point}>
                                <span>{paySuccessResult.points}</span>
                                <span>积分 已发放</span>
                            </div>
                        )}
                        {paySuccessResult?.skuExpireAt && (
                            <div className={styles.time}>
                                {paySuccessResult.skuStartAt && (
                                    <span>
                                        将在
                                        {formatUnixDate(paySuccessResult.skuStartAt, 'YYYY.MM.DD')}
                                        生效，
                                    </span>
                                )}
                                <span>会员有效期至 </span>
                                <span>
                                    {formatUnixDate(paySuccessResult.skuExpireAt, 'YYYY.MM.DD')}
                                </span>
                            </div>
                        )}
                        {showRightsPendingTip && (
                            <div className={styles.warning}>权益预计于2分钟内生效</div>
                        )}
                    </div>
                ) : (
                    <PaymentPanel
                        className={styles.paymentPanelContainer}
                        session={session}
                        agreement={agreement}
                        onEvent={handlePaymentEvent}
                    />
                )}
            </div>
        </Modal>
    );
};
